<template>
  <div>
    <slot v-bind:open="open" />
    <Modal :show="show" @closeModal="close" size="xl">
      <div>
        <h1 class="text-xl font-medium">Split Delivery Note #{{ note.order_id }}-{{ note.id }}</h1>
      </div>
      <div class="grid grid-cols-2 gap-3 mt-4">
        <div class="border rounded-md">
          <div class="capitalize px-4 pt-3">
            <h1 class="text-xl font-medium">{{ note.type === 'collection' ? 'Collection by Customer' : 'To be Delivered' }}</h1>
          </div>
          <NoteDetailItems
            ref="originalItem"
            v-model="form.actual"
            :key="note.type"
            :type="note.type"
            :warehouses="warehouses"
            @selected="onSelectItem"
            @removed="onRemoveItem"
            :order-id="note.order_id"
            enabled
            :handover-items="form.items"
          />
        </div>
        <div class="border rounded-md capitalize">
          <div class="px-4 pt-3">
            <SelectInput v-model="form.splits.type" @input="onChangeSplitType">
              <option value="collection">Collection by Customer</option>
              <option value="delivery">To be Delivered</option>
            </SelectInput>
          </div>
          <NoteDetailItems
            :style="note.type === 'delivery' ? {marginTop: '-19px'}: {}"
            v-model="form.splits"
            :selected="form.splits.items"
            :key="'split-'+form.splits.type"
            :type="note.type"
            :warehouses="warehouses"
            :order-id="note.order_id"
            @quantity-change="onQuantityChange"
            :errors="form.errors"
            is-split
            :location="form.splits.delivery_location"
            :getWarehouseListFor="getWarehouseListFor"
          />
          <div v-if="form.splits.type === 'delivery'" class="mt-3 border-t px-4 py-3">
            <div class="grid grid-cols-2 gap-3">
              <div :class="{'col-span-full': form.splits.delivery_location !== 'island' }">
                <SelectInput
                  v-model="form.splits.delivery_location"
                  label="Delivery Location"
                  :errors="form.errors['splits.delivery_location']"
                  @input="onDeliveryLocationSelect"
                >
                  <option v-for="(label, value) in locations" :value="value">{{ label }}</option>
                </SelectInput>

              </div>
              <div v-if="form.splits.delivery_location === 'island'">
                <SelectInput
                  v-model="form.splits.vessel_location"
                  label="Vessel Location"
                  :errors="form.errors['splits.vessel_location']"
                >
                  <option v-for="location in vesselLocations.island" :value="location">{{ location }}</option>
                </SelectInput>
              </div>
              <div class="col-span-full">
                <template v-if="form.splits.delivery_location !== 'island'">
                  <FormInput
                    type="text"
                    v-model="form.splits.delivery_address"
                    label="Delivery Address"
                    :errors="form.errors['splits.delivery_address']"
                  />
                </template>
                <SelectInput v-else v-model="form.splits.boat_id" label="Vessel" :errors="form.errors['splits.boat_id']">
                  <option v-for="vessel in boats" :value="vessel.id">{{ vessel.name }}</option>
                </SelectInput>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-4 flex gap-x-3 justify-end">
        <button type="button" class="btn-gray" @click="close">Close</button>

        <LoadingButton
          :loading="form.processing"
          :disabled="form.processing || !form.splits.items.length"
          class="btn-primary"
          @click="makeSplit"
        >
          Split
        </LoadingButton>
      </div>
    </Modal>
  </div>
</template>

<script>
import NoteDetailItems from "../../../DeliveryNote/NoteDetailItems.vue";
import Modal from "../../../../../Shared/Modal.vue";
import SelectInput from "../../../../../Shared/SelectInput.vue";
import FormInput from "../../../../../Shared/Form/FormInput.vue";
import LoadingButton from "../../../../../Shared/LoadingButton.vue";
import {mapFormValue} from "../../../../../Utils";
import {debounce} from "lodash";

export default {
  name: 'SplitDeliveryNoteModal',
  components: {LoadingButton, FormInput, SelectInput, Modal, NoteDetailItems},
  props: {
    warehouses: Array,
    note: Object,
    vesselLocations: Object,
    locations: Object,
    order: Object,
    getWarehouseListFor: Function,
  },

  data() {
    return {
      modalPersistKey: `split-note-modal-${this.note.id}`,
      show: false,
      boats: [],
      form: this.$inertia.form({
        split: true,
        note_id: this.note.id,
        actual: {
          type: this.note.type,
          items: [],
        },
        items: [],
        splits: {
          type: null,
          delivery_location: null,
          vessel_location: null,
          boat_id: null,
          delivery_address: null,
          items: [],
        },
      })
    }
  },

  computed: {
    getSplitType() {
      return this.note.type === 'collection' ? 'delivery' : 'collection'
    },
  },
  methods: {
    makeSplit() {
      this.form.post(this.route('delivery-notes.split'), {
        preserveState: 'errors',
        onSuccess: () => this.close()
      })
    },
    onDeliveryLocationSelect(location) {
      if (location === 'island') {
        this.form.splits.vessel_location = this.order.vessel_location
        this.form.splits.boat_id = this.order.boat_id
        this.fetchBoatList()
      } else {
        this.form.splits.boat_id = null
        this.form.splits.vessel_location = null
        this.form.splits.delivery_address = this.order.delivery_address
      }
    },
    fetchBoatList() {
      this.$http.get(route('ajax.boat-list'))
        .then(resp => {
          this.boats = resp.data
        })
    },
    onChangeSplitType() {
      if (this.form.splits.type === 'collection') {
        this.form.splits.boat_id = null
        this.form.splits.vessel_location = null
        this.form.splits.delivery_address = null
      } else {
        this.form.splits.delivery_location = this.order.delivery_location
        if (this.form.splits.delivery_location === 'island') {
          this.form.splits.vessel_location = this.order.vessel_location
          this.form.splits.boat_id = this.order.boat_id
          this.fetchBoatList()
        } else {
          this.form.splits.delivery_address = this.order.delivery_address
        }
      }
    },
    onSelectItem(item) {
      if (!this.form.splits.items.some(itm => itm.id === item.id)) {
        this.form.splits.items.push(item)
      } else {
        this.partial = true
        if (this.partial) {
          const index = this.form.items.findIndex(val => val.id === item.id);
          if (index !== -1) {
            this.form.items.splice(index, 1);
          } else {
            if (this.form.items.length === 0 || this.form.items[0].warehouse_id === item.warehouse_id) {
              this.form.items.push(item);
            }
          }
        }

      }
    },
    onRemoveItem(item) {
      const index = this.form.splits.items.findIndex(itm => itm.id === item.id)
      const splitItem = this.form.splits.items.find(itm => itm.id === item.id)
      const actualIndex = this.form.actual.items.findIndex(itm => itm.id === item.id)
      if (this.form.splits.items.splice(index, 1)) {
        this.form.actual.items[actualIndex].quantity_pending = splitItem.quantity_pending
      }
    },

    onQuantityChange(splitItem) {
      this.$refs.originalItem.reduceQuantity(splitItem)
    },

    open() {
      this.form.splits.type = this.getSplitType
      this.form.splits.vessel_location = null
      this.form.splits.delivery_location = null
      this.form.splits.items = []
      this.form.actual.items = this.note.items.filter(itm => itm.delivered !== true).map(item => {
        return {
          ...item,
          selected: false,
        }
      })
      if (this.form.splits.delivery_location === 'island') {
        this.fetchBoatList()
      }
      this.show = true
      localStorage.setItem(this.modalPersistKey, JSON.stringify(this.form))
      const url = new URL(window.location)
      url.searchParams.set('split', this.note.id)
      window.history.pushState({}, '', url)
    },
    close() {
      this.form.clearErrors()
      this.form.reset()
      this.show = false
      const url = new URL(window.location);
      url.searchParams.delete('split');
      window.history.replaceState({}, '', url);
      localStorage.removeItem(this.modalPersistKey)
    },

    restorePersistantState() {
      const params = new URLSearchParams(window.location.search);

      if (parseInt(params.get('split')) === this.note.id) {
        const state = localStorage.getItem(this.modalPersistKey);
        if (state) {
            mapFormValue(this.form, JSON.parse(state));
        }
        if (this.form.splits.delivery_location === 'island') {
          this.fetchBoatList()
        }

        this.show = parseInt(params.get('split')) === this.note.id
      }
    }
  },

  watch: {
    form: {
      handler: debounce(function (formState) {
        localStorage.setItem(this.modalPersistKey, JSON.stringify(this.form.data()));
      }, 300),
      deep: true,
    },
  },

  mounted() {
    this.restorePersistantState()
  },

  beforeDestroy() {
    localStorage.removeItem(this.modalPersistKey)
  }
}
</script>
